<template>
  <div class="tw:flex tw:flex-col tw:h-screen tw:max-h-screen tw:relative">
    <component
      :is="components[currentTopBarComponent]"
      class="tw:flex-none"
      :overlay="overlayTopbar"
    ></component>

    <ScrollPanel
      :key="route.fullPath"
      class="layout-main-container tw:grow tw:h-[calc(100vh-206px)]"
      :dt="{
        bar: {
          background: '{primary.color}',
          opacity: 0.5,
          hoverOpacity: 0.7,
          size: '4px',
        },
      }"
    >
      <section class="tw:px-[16px]" :class="{ 'tw:pt-0': currentTopBarComponent === 'AppTopbar' }">
        <router-view v-slot="{ Component, route: routerRoute }">
          <transition :name="routerRoute.meta?.transition || 'fade'" mode="out-in" appear>
            <div v-if="show" :key="routerRoute.fullPath">
              <component :is="Component" :key="routerRoute.fullPath"></component>
            </div>
          </transition>
        </router-view>
      </section>
    </ScrollPanel>
    <AppBottombar class="tw:h-[93px] tw:flex-none"></AppBottombar>
  </div>
  <Drawer v-model:visible="leftDrawerOpen" block-scroll>
    <template #header>
      <div class="tw:flex tw:items-center tw:gap-2 tw:mt-[40px]">
        <Avatar :image="avatarUrl" shape="circle" class="tw:h-[32px] tw:w-[32px]" />
        <span class="tw:font-bold tw:text-[16px] tw:leading-[18px]">{{ displayName }}</span>
      </div>
    </template>
    <template #closebutton>
      <Button
        icon="pi pi-times"
        severity="contrast"
        variant="text"
        rounded
        aria-label="Close"
        class="tw:mt-[40px]"
        @click="toggleLeftDrawer"
      />
    </template>
    <p class="tw:font-semibold tw:text-[#8A8A8C] tw:text-[14px] tw:leading-[16px]">
      Empowering patients, optimizing cancer care with AI and data-driven insights
    </p>
    <hr class="tw:border-t tw:border-[#eee] tw:my-4" />
    <ul class="tw:my-4 tw:mb-8 tw:space-y-3">
      <li
        class="tw:font-medium tw:text-black tw:text-[16px] tw:leading-[18px] tw:mb-[10px]"
        @click="goToUpdateTreatment"
      >
        <i class="pi pi-pen-to-square tw:mr-[5px]"></i> Update Treatment
      </li>
    </ul>
    <template #footer>
      <div class="tw:flex tw:items-center tw:gap-2">
        <Button label="Account" icon="pi pi-user" class="tw:flex-auto" outlined></Button>
        <Button
          label="Logout"
          icon="pi pi-sign-out"
          class="tw:flex-auto"
          severity="danger"
          text
          @click="handleLogoutClick"
        ></Button>
      </div>
    </template>
  </Drawer>
  <Toast position="top-right" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TopbarTypeEnum } from '@/helpers/enums';
import AppTopbar from '@/layouts/components/AppTopbar.vue';
import AppTopbarNavigation from '@/layouts/components/AppTopbarNavigation.vue';
import AppBottombar from '@/layouts/components/AppBottombar.vue';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { storeToRefs } from 'pinia';

const { leftDrawerOpen } = storeToRefs(useAppStore());
const { toggleLeftDrawer } = useAppStore();
const { ME } = storeToRefs(useAuthStore());
const { signOut } = useAuthStore();

const route = useRoute();
const router = useRouter();
const { getOverlayTopbar } = storeToRefs(useAppStore());
const components = { AppTopbar, AppTopbarNavigation };

const show = ref(true);

const currentTopBarComponent = computed(() => route.meta?.topbarType ?? TopbarTypeEnum.DEFAULT);

const goToUpdateTreatment = () => {
  router.push({
    name: 'treatment',
  });
};

const handleLogoutClick = async () => {
  await signOut();
  toggleLeftDrawer();
  globalThis.location.href = '/';
};

const overlayTopbar = computed(() => getOverlayTopbar.value);

const avatarUrl = computed(() => ME.value?.avatar || '/images/avatars/profile.png');
const displayName = computed(() => ME.value?.patientName || ME.value?.name || 'Patient');

// 5. VueJS Hooks
onMounted(async () => {});
</script>

<style lang="scss" scoped>
.fade {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.5s;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}

.slide-fade {
  &-enter-active {
    transition: all 0.3s ease-out;
  }

  &-leave-active {
    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
  }

  &-enter-from,
  &-leave-to {
    transform: translateX(20px);
    opacity: 0;
  }
}
</style>
